var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, forwardRef, useState } from 'react';
import { composeRef } from 'rc-util/lib/ref';
import { Button } from '../Button';
import { TextField } from '../TextField';
import { Icon } from '../Icon';
import './SearchField.styles.scss';
import { Popup } from '../Popup';
import { Dropdown } from '../Dropdown';
import { getDropdownPosition } from '../Dropdown/Dropdown.utils';
var EMappedInputEventCodes;
(function (EMappedInputEventCodes) {
    EMappedInputEventCodes["ARROW_DOWN"] = "ArrowDown";
    EMappedInputEventCodes["ARROW_UP"] = "ArrowUp";
    EMappedInputEventCodes["ENTER"] = "Enter";
    EMappedInputEventCodes["ESC"] = "Escape";
})(EMappedInputEventCodes || (EMappedInputEventCodes = {}));
var SearchField = forwardRef(function SearchField(_a, ref) {
    var _b;
    var autoFocus = _a.autoFocus, disabled = _a.disabled, dropdownPosition = _a.dropdownPosition, helperText = _a.helperText, helperTextTooltipPlacement = _a.helperTextTooltipPlacement, _c = _a.hideIconButton, hideIconButton = _c === void 0 ? false : _c, _d = _a.hideLabel, hideLabel = _d === void 0 ? false : _d, inputValue = _a.inputValue, label = _a.label, name = _a.name, onBlur = _a.onBlur, onChange = _a.onChange, onFocus = _a.onFocus, onKeyDown = _a.onKeyDown, onKeyUp = _a.onKeyUp, onPaste = _a.onPaste, onSelectResultOption = _a.onSelectResultOption, _e = _a.optionButtonText, optionButtonText = _e === void 0 ? 'Adicionar' : _e, placeholder = _a.placeholder, _f = _a.required, required = _f === void 0 ? false : _f, rounded = _a.rounded, searchResultOptions = _a.searchResultOptions, supportMessage = _a.supportMessage;
    var containerRef = useRef(null);
    var optionsRef = useRef(null);
    var inputRef = useRef(null);
    var _g = useState(), currentDropdownPosition = _g[0], setCurrentDropdownPosition = _g[1];
    var _h = useState(searchResultOptions), filteredResultOptions = _h[0], setFilteredResultOptions = _h[1];
    var onOpenOptions = function () {
        if (disabled !== null && disabled !== void 0 ? disabled : !searchResultOptions)
            return;
        setCurrentDropdownPosition(dropdownPosition !== null && dropdownPosition !== void 0 ? dropdownPosition : getDropdownPosition(containerRef));
        setOptionHoveredIdx(-1);
        if (searchString) {
            filterOptions(searchString);
        }
    };
    var onSelectOption = function (option, e) {
        !!onSelectResultOption && onSelectResultOption(option);
        clearSearchField(e);
        onCloseOptions();
    };
    var onCloseOptions = function () {
        setFilteredResultOptions(searchResultOptions);
        setCurrentDropdownPosition(undefined);
        cleanIsHover();
    };
    var _j = useState(''), searchString = _j[0], setSearchString = _j[1];
    var clearSearchField = function (event) {
        if (!inputRef.current) {
            return;
        }
        var currentTarget = inputRef.current.cloneNode(true);
        event = Object.create(event, {
            target: { value: __assign(__assign({}, currentTarget), { value: '' }) },
            currentTarget: { value: currentTarget },
        });
        onChangeSearchString(event);
    };
    useEffect(function () {
        if (searchString.length > 0 &&
            searchResultOptions &&
            searchResultOptions.length > 0 &&
            !currentDropdownPosition) {
            onOpenOptions();
        }
    }, [searchResultOptions]);
    var onChangeSearchString = function (event) {
        var search = event.target.value;
        if (search !== '') {
            if (!currentDropdownPosition &&
                !!searchResultOptions &&
                searchResultOptions.length > 0) {
                onOpenOptions();
            }
            filterOptions(search);
        }
        else {
            onCloseOptions();
        }
        setSearchString(search);
        !!onChange && onChange(event);
    };
    var checkIfInputValueChangedFromOut = function () {
        if (inputValue !== undefined && searchString !== inputValue) {
            setSearchString(inputValue);
        }
    };
    useEffect(function () {
        checkIfInputValueChangedFromOut();
    }, [inputValue]);
    var filterOptions = function (newValue) {
        searchResultOptions === null || searchResultOptions === void 0 ? void 0 : searchResultOptions.sort(function (a, b) { return a.label.localeCompare(b.label); });
        var filteredOptions = searchResultOptions === null || searchResultOptions === void 0 ? void 0 : searchResultOptions.filter(function (opt) {
            return opt.label.toLowerCase().includes(newValue.toLowerCase());
        });
        cleanIsHover();
        setFilteredResultOptions(filteredOptions);
    };
    var _k = useState(-1), optionHoveredIdx = _k[0], setOptionHoveredIdx = _k[1];
    var updateCustomSelectHovered = function (newIndex) {
        var _a, _b;
        var prevOption = (_a = optionsRef.current) === null || _a === void 0 ? void 0 : _a.children[optionHoveredIdx];
        var option = (_b = optionsRef.current) === null || _b === void 0 ? void 0 : _b.children[newIndex];
        if (prevOption) {
            prevOption.classList.remove('is-hover');
        }
        if (option) {
            option.classList.add('is-hover');
            option.scrollIntoView({ block: 'nearest' });
        }
        setOptionHoveredIdx(newIndex);
    };
    var onInputKeyDown = function (event) {
        var _a, _b;
        !!onKeyDown && onKeyDown(event);
        if (!Object.values(EMappedInputEventCodes).find(function (code) { return code === event.code; })) {
            return;
        }
        event.preventDefault();
        switch (event.code) {
            // press down -> go next
            case EMappedInputEventCodes.ARROW_DOWN: {
                if (filteredResultOptions &&
                    optionHoveredIdx < filteredResultOptions.length - 1) {
                    updateCustomSelectHovered(optionHoveredIdx + 1);
                }
                return;
            }
            // press up -> go previous
            case EMappedInputEventCodes.ARROW_UP: {
                if (filteredResultOptions && optionHoveredIdx === -1) {
                    updateCustomSelectHovered(filteredResultOptions.length - 1);
                }
                else if (optionHoveredIdx > 0) {
                    updateCustomSelectHovered(optionHoveredIdx - 1);
                }
                return;
            }
            // press Enter -> select the option
            case EMappedInputEventCodes.ENTER: {
                if (!currentDropdownPosition) {
                    return;
                }
                var option = (_a = optionsRef.current) === null || _a === void 0 ? void 0 : _a.children[optionHoveredIdx];
                var optionId_1 = option === null || option === void 0 ? void 0 : option.getAttribute('value');
                var selectedOption = filteredResultOptions === null || filteredResultOptions === void 0 ? void 0 : filteredResultOptions.find(function (opt) { return opt.id === optionId_1; });
                selectedOption &&
                    !selectedOption.disabled &&
                    onSelectOption(selectedOption, event);
                return;
            }
            // press ESC -> close
            case EMappedInputEventCodes.ESC: {
                onCloseOptions();
                (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.blur();
            }
        }
    };
    var cleanIsHover = function () {
        var _a;
        var hoveredOption = (_a = optionsRef.current) === null || _a === void 0 ? void 0 : _a.children[optionHoveredIdx];
        if (hoveredOption) {
            hoveredOption.classList.remove('is-hover');
            setOptionHoveredIdx(-1);
        }
    };
    var checkOutsideClick = function (event) {
        if (!!event &&
            !!containerRef.current &&
            !containerRef.current.contains(event.target)) {
            onCloseOptions();
        }
    };
    useEffect(function () {
        window.addEventListener('click', checkOutsideClick);
        return function () {
            window.removeEventListener('click', checkOutsideClick);
        };
    });
    var getTextFieldClassName = function () {
        var classList = [];
        if (currentDropdownPosition) {
            classList.push("hide-border-".concat(currentDropdownPosition));
        }
        return classList.join(' ');
    };
    var textFieldClassName = getTextFieldClassName();
    return (_jsx(Popup, __assign({ trigger: 'click', popupPlacement: "bottomLeft", popupVisible: !!currentDropdownPosition && !!filteredResultOptions, onPopupVisibleChange: function (visible) {
            if (visible) {
                onCloseOptions();
            }
        }, popupContainerScreenPosition: (_b = containerRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().left, popup: _jsx(_Fragment, { children: currentDropdownPosition && filteredResultOptions && (_jsxs(Dropdown, __assign({ containerRef: containerRef, dropdownPosition: currentDropdownPosition, inputRef: inputRef }, { children: [filteredResultOptions.length > 0 && (_jsx("ul", __assign({ className: "hefesto-search-field-dropdown-options", ref: optionsRef, onMouseOver: cleanIsHover }, { children: filteredResultOptions.map(function (option, idx) { return (_jsx(DropdownItem, { option: option, inputRef: inputRef, onSelectOption: onSelectOption, optionButtonText: optionButtonText, searchString: searchString }, idx)); }) }))), filteredResultOptions.length === 0 && (_jsx("ul", __assign({ className: "hefesto-search-field-dropdown-options no-options", onClick: function () { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); } }, { children: _jsx("span", { children: "Nenhum resultado encontrado" }) })))] }))) }) }, { children: _jsx(TextField, { autoFocus: autoFocus, "data-testid": "hefesto-search-field", label: label, type: "search", supportMessage: supportMessage, helperText: helperText, helperTextTooltipPlacement: helperTextTooltipPlacement, hideLabel: hideLabel, name: name, iconPosition: "left", icon: _jsx(Icon, { option: "Search" }), onChange: onChangeSearchString, onKeyDown: onInputKeyDown, onKeyUp: onKeyUp, onPaste: onPaste, onFocus: function (event) {
                !!searchString && onOpenOptions();
                !!onFocus && onFocus(event);
            }, onBlur: onBlur, placeholder: placeholder, value: searchString, rounded: rounded, containerRef: containerRef, ref: composeRef(ref, inputRef), className: textFieldClassName, disabled: disabled, required: required, rightButton: !!searchString &&
                !hideIconButton && (_jsx(Button, { type: "button", name: "Limpar", ariaLabel: "Limpar o campo de busca", onClick: clearSearchField, iconPosition: 'left', icon: _jsx(Icon, { option: "XCircle", variant: "primary" }), variant: "none" })) }) })));
});
var DropdownItem = function (_a) {
    var inputRef = _a.inputRef, onSelectOption = _a.onSelectOption, option = _a.option, optionButtonText = _a.optionButtonText, searchString = _a.searchString;
    var getOptionTextWithSearchString = function (text) {
        if (text.toUpperCase().includes(searchString.toUpperCase())) {
            var startPos = text.toUpperCase().indexOf(searchString.toUpperCase());
            return (_jsxs(_Fragment, { children: [text.substring(0, startPos), _jsx("strong", { children: text.substring(startPos, startPos + searchString.length) }), text.substring(startPos + searchString.length, startPos + text.length)] }));
        }
        else {
            return _jsx(_Fragment, { children: text });
        }
    };
    return (_jsxs("li", __assign({ className: "hefesto-search-field-option", value: option.id, onClick: function (e) {
            var _a;
            e.stopPropagation();
            if (option.disabled) {
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                return;
            }
            onSelectOption(option, e);
        } }, { children: [_jsxs("div", { children: [_jsx("label", __assign({ className: 'hefesto-search-field-option__label' }, { children: getOptionTextWithSearchString(option.label) })), option.disabled && option.disabledText && (_jsxs("label", __assign({ className: "hefesto-search-field-option__disabled-label" }, { children: ["*", option.disabledText] })))] }), !option.disabled && !option.disabledText && (_jsx(Button, __assign({ name: "btn-".concat(option.id) }, { children: optionButtonText })))] })));
};
export default SearchField;
